<template>
  <div class="w-100 grid-container">
    <vt-side-panel :visible="showSidePanel">
      <configuration-sidepanel
        :association-type="ASSOCIATION_TYPE.RESPONSIBILITY.key"
        :show-center-filter="true"
        :sidepanel-title="sidepanelTitle"
        :association="$tc('configuration.modal.responsibility', selectedPersonnelResponsabilities.length)"
        :icon="'far fa-hospital-user'"
        :list="allSelectedPersonnelResponsibilities"
        @closeConfigurationSidepanel="showSidePanel = false"
        @update="showConfigurationPersonnelForm($t('configuration.actions.update'), clickedRow, $event); showSidePanel = false"
      />
    </vt-side-panel>
    <configuration-section-header
      class="my-0"
      :section-icon="'far fa-hospital-user'"
      :section-title="$t('configuration.sections.personnel')"
      :section-subtitle="msgSelectedRows"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          class="far fa-check-circle"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          class="far fa-times-circle"
          aria-hidden="true"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="selectedRows.length < 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deletePersonnel(selectedRows)"
        @shortkey="deletePersonnel(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="selectedRows.length !== 1"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showConfigurationPersonnelForm($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showConfigurationPersonnelForm($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showConfigurationPersonnelForm($t('configuration.actions.add'))"
        @shortkey="showConfigurationPersonnelForm($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        v-if="columnDefs"
        class="configuration-grid people-personnel-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="configurationPersonnelFormWrapper">
      <configuration-personnel-form
        ref="configurationPersonnelForm"
        :action-title="actionTitle"
        @update="patchPersonnel($event)"
        @create="createPersonnel($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import configurationPersonnelGridColumns from '@/modules/configurationModule/detail/peopleManagement/personnel/configurationPersonnelGridColumns'
import ConfigurationPersonnelForm from '@/modules/configurationModule/detail/peopleManagement/personnel/ConfigurationPersonnelForm'
import ConfigurationSidepanel from '@/modules/configurationModule/detail/ConfigurationSidepanel'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import PersonnelResponsibilityConfigurationServices from '@/api/resources/configuration/peopleManagement/personnel/PersonnelResponsibilityConfigurationServices'
import PersonnelConfigurationServices from '@/api/resources/configuration/peopleManagement/personnel/PersonnelConfigurationServices'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'
import { ASSOCIATION_TYPE } from '@/api/resources/configuration/peopleManagement/AssociationTypes'
import { uniqBy, isEmpty } from 'lodash'

export default {
    name: 'PeoplePersonnelGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ConfigurationPersonnelForm,
        ConfigurationSidepanel
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.peopleManagement.personnel',
            showSidePanel: false,
            sidepanelTitle: null,
            actionTitle: null,
            selectedRows: [],
            mode: {
                size: 25
            },
            clickedRow: null,
            columnDefs: configurationPersonnelGridColumns(),
            selectedPersonnelResponsabilities: [],
            allSelectedPersonnelResponsibilities: []
        }
    },
    computed: {
        msgSelectedRows() {
            return this.selectedRows.length > 0 ? `${this.selectedRows.length} ${this.$tc('global.selectedElements', this.selectedRows.length)}` : ''
        },
        rowHeight() {
            return this.mode?.size
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                onRowClicked: this.onRowClicked,
                getContextMenuItems: this.getContextMenuItems,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getRowHeight: () => this.rowHeight,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged
            }
        },
        ASSOCIATION_TYPE() {
            return ASSOCIATION_TYPE
        },
        hasActivePersonnel() {
            return !!this.selectedRows.find(personnel => personnel.active)
        },
        hasDeactivePersonnel() {
            return !!this.selectedRows.find(personnel => !personnel.active)
        },
        hasAllActivated() {
            return this.selectedRows.length === (this.selectedRows.filter(personnel => personnel.active).length)
        },
        hasAllDeactivated() {
            return this.selectedRows.length === (this.selectedRows.filter(personnel => !personnel.active).length)
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActivePersonnel && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactivePersonnel && !this.hasAllActivated
        }
    },
    async mounted() {
        if (this.$route.params.personnelId) {
            const response = await this.$_toCallUnique(PersonnelConfigurationServices.getPersonnelById(this.$route.params.personnelId))
            if (response) {
                this.showConfigurationPersonnelForm(this.$t('configuration.actions.update'), response)
            }
        }
    },
    methods: {
        async activateDeactivateSelection(option, selectionBatch) {
            const personnelToPatch = selectionBatch.filter(personnel => personnel.active !== option).map(personnel => {
                return {
                    active: option,
                    id: personnel.id
                }
            })
            if (personnelToPatch.length) {
                const response = await this.$_toCallUnique(PersonnelConfigurationServices.patchPersonnelBatch(personnelToPatch))
                if (response) {
                    if (personnelToPatch.length > 1) this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: personnelToPatch.length }))
                    else this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                    this.reloadData()
                }
            }
        },
        async patchPersonnel(...personnelBatch) {
            const personnelUpdateRequest = personnelBatch.map(personnel => {
                let patchRequest = {
                    id: personnel.id,
                    active: personnel.active,
                    allowsCaseValidationByOtherUsers: personnel.allowsCaseValidationByOtherUsers,
                    code: personnel.code,
                    contactInformation: !isEmpty(personnel.contactInformation) ? personnel.contactInformation : null,
                    defaultLaboratoryId: personnel.defaultLaboratory.labId,
                    expectedAnnualUCLValue: personnel.expectedAnnualUCLValue,
                    middleName: personnel.middleName,
                    name: personnel.name,
                    surname1: personnel.surname1,
                    surname2: personnel.surname2
                }
                if (personnel.jobCode) {
                    patchRequest.jobCode = personnel.jobCode
                }
                return patchRequest
            })
            const response = await this.$_toCallUnique(PersonnelConfigurationServices.patchPersonnelBatch(personnelUpdateRequest))
            if (response?.length !== personnelBatch.length) {
                this.$refs.configurationPersonnelForm.hide()
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                this.reloadData()
            }
        },
        async createPersonnel(personnel) {
            const personnelCreateRequest = {
                active: personnel.active,
                allowsCaseValidationByOtherUsers: personnel.allowsCaseValidationByOtherUsers,
                code: personnel.code,
                contactInformation: !isEmpty(personnel.contactInformation) ? personnel.contactInformation : null,
                defaultLaboratoryId: personnel.defaultLaboratory.labId,
                expectedAnnualUCLValue: personnel.expectedAnnualUCLValue,
                jobCode: personnel.job.code,
                middleName: personnel.middleName,
                name: personnel.name,
                surname1: personnel.surname1,
                surname2: personnel.surname2
            }
            const response = await this.$_toCallUnique(PersonnelConfigurationServices.createPersonnel(personnelCreateRequest))
            if (response) {
                await this.$refs.configurationPersonnelForm.setPersonnelResponsibilities(response)
                this.actionTitle = this.$t('configuration.actions.update')
                this.$_showToastSuccess(this.$t('configuration.toastMessages.created'))
                this.reloadData()
            }
        },
        async deletePersonnelBatch(personnelBatch) {
            const personnelToDelete = personnelBatch.map(personnel => personnel.id)
            const response = await this.$_toCallUnique(PersonnelConfigurationServices.deletePersonnelBatch(personnelToDelete))
            if (response?.length !== personnelBatch.length) {
                this.$refs.configurationPersonnelFormWrapper.hide()
                if (personnelToDelete.length > 1) this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 2, { elements: personnelToDelete.length }))
                else this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
                this.reloadData()
            }
        },
        getContextMenuItems(params) {
            if (!params?.node) {
                return []
            } else {
                return [
                    {
                        name: this.$t('configuration.actions.update'),
                        cssClasses: this.selectedRows.length > 1 ? ['not-show'] : '',
                        action: () => this.showConfigurationPersonnelForm(this.$t('configuration.actions.update'), params?.node.data)
                    },
                    {
                        name: this.selectedRows.length ? this.$t('configuration.actions.deleteSelection') : this.$t('configuration.actions.delete'),
                        action: () => {
                            const personnelToDelete = this.selectedRows.length ? this.selectedRows : [params?.node.data]
                            this.deletePersonnel(personnelToDelete)
                        }
                    },
                    {
                        name: params?.node.data.active ? this.$t('configuration.actions.deactivate') : this.$t('configuration.actions.activate'),
                        cssClasses: this.selectedRows.length ? ['not-show'] : '',
                        action: () => this.activateDeactivateSelection(!params.node?.data.active, [params?.node.data])
                    },
                    {
                        name: this.$t('configuration.actions.activate'),
                        cssClasses: this.canActivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(true, this.selectedRows)
                    },
                    {
                        name: this.$t('configuration.actions.deactivate'),
                        cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(false, this.selectedRows)
                    },
                    'separator',
                    {
                        name: params?.node.data.user ? this.$t('configuration.actions.updateUser') : this.$t('configuration.modal.goToUser'),
                        action: () => {
                            const goToRoute = { name: 'PeopleUserapp', params: { actions: this.getActions(params?.node.data) } }
                            this.$router.push(goToRoute)
                        }
                    }
                ]
            }
        },
        getActions(entity) {
            let actionObj = {}
            if (entity.user) {
                actionObj = { updateUser: { userId: entity.user.id } }
            } else {
                actionObj = { createUser: { personnelId: entity.id } }
            }
            return actionObj
        },
        setColumns(columns) {
            let columnDefs = columns
            if (!this.globalParameters.middleNameSupported) {
                columnDefs = columns.filter(col => col.field !== 'middleName')
            }
            return columnDefs
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        async onRowClicked(params) {
            this.updateLoading(true)
            const [err, response] = await this.toCall(PersonnelResponsibilityConfigurationServices.getPersonnelResponsibilitiesByPersonnelId(params?.node.data.id))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.clickedRow = params?.node.data
            this.selectedPersonnelResponsabilities = uniqBy(response, 'responsibility.id').map(item => item['responsibility'])
            this.allSelectedPersonnelResponsibilities = response
            this.showSidePanel = true
            this.sidepanelTitle = params?.node.data.fullName
        },
        onGridReady({ api }) {
            this.gridApi = api
            this.gridApi.setColumnDefs(this.setColumns(this.columnDefs))
            api.setServerSideDatasource(new PaginatorDatasource(PersonnelConfigurationServices))
        },
        showConfigurationPersonnelForm(title, selectedPersonnel, associationType) {
            this.actionTitle = title
            this.$refs.configurationPersonnelFormWrapper.show([selectedPersonnel, associationType])
        },
        async deletePersonnel(personnel) {
            if (personnel.length) {
                const promise = await this.showConfirmationModalGlobal(`${this.$t('configuration.grid.deleteConfirmation')} ${personnel.length} ${this.$tc('configuration.grid.element', personnel.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    await this.deletePersonnelBatch(personnel)
                }
            }
        },
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(PersonnelConfigurationServices))
        }
    }
}
</script>
